import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { CrmService } from '../../../services/crm.service';
import { Activity, ActivityType } from '../../../classes/crm';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-customer-activities-timeline',
  templateUrl: './customer-activities-timeline.component.html',
  styleUrls: ['./customer-activities-timeline.component.scss']
})
export class CustomerActivitiesTimelineComponent implements OnInit, OnChanges, OnDestroy {
  @Input() customerdata: any;
  @Output() activityUpdated = new EventEmitter<any>();

  activities: Activity[] = [];
  activityTypes: ActivityType[] = [];
  editingActivity: Activity | null = null;
  showCreateForm: boolean = false;
  loading: boolean = false;

  // Form data
  newActivity = {
    type_id: 0,
    description: '',
    activity_date: '',
    follow_up_date: '',
    outcome: ''
  };

  private destroy$ = new Subject<void>();

  constructor(
    private globalSearchService: GlobalSearchService,
    private crmService: CrmService
  ) { }

  ngOnInit(): void {
    this.loadActivityTypes();
    if (this.customerdata) {
      this.loadActivities();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerdata'] && this.customerdata && !changes['customerdata'].firstChange) {
      this.loadActivities();
    }
  }

  loadActivityTypes(): void {
    this.globalSearchService.activity_types
      .pipe(takeUntil(this.destroy$))
      .subscribe(types => {
        this.activityTypes = types || [];
      });
  }

  loadActivities(): void {
    if (!this.customerdata?.debtorno) return;

    this.loading = true;
    this.crmService.getActivitiesByCustomer(
      this.customerdata.debtorno,
      this.customerdata.branchcode || 'MAIN'
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (activities: Activity[]) => {
        this.activities = (activities || []).sort((a, b) => 
          new Date(b.activity_date || b.created_at || '').getTime() - 
          new Date(a.activity_date || a.created_at || '').getTime()
        );
        this.loading = false;
      },
      error => {
        console.error('Error loading activities:', error);
        this.loading = false;
        alert('Failed to load activities. Please try again.');
      }
    );
  }

  createActivity(): void {
    if (!this.customerdata?.debtorno) return;

    const activityData = {
      debtorno: this.customerdata.debtorno,
      branchcode: this.customerdata.branchcode || 'MAIN',
      type_id: this.newActivity.type_id,
      description: this.newActivity.description,
      activity_date: this.newActivity.activity_date,
      follow_up_date: this.newActivity.follow_up_date,
      outcome: this.newActivity.outcome
    };

    this.crmService.createActivity(activityData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.loadActivities();
          this.resetForm();
          this.showCreateForm = false;
          this.activityUpdated.emit(result);
        },
        error => {
          console.error('Error creating activity:', error);
          alert('Failed to create activity. Please try again.');
        }
      );
  }

  updateActivity(activity: Activity): void {
    this.crmService.updateActivity(activity.id!, activity)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loadActivities();
          this.editingActivity = null;
          this.activityUpdated.emit(activity);
        },
        error => console.error('Error updating activity:', error)
      );
  }

  deleteActivity(activity: Activity): void {
    if (confirm('Are you sure you want to delete this activity?')) {
      this.crmService.deleteActivity(activity.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.loadActivities();
            this.activityUpdated.emit(activity);
          },
          error => console.error('Error deleting activity:', error)
        );
    }
  }

  getActivityTypeName(typeId: number): string {
    const type = this.activityTypes.find(t => t.id === typeId);
    return type?.name || 'Unknown';
  }

  getActivityTypeColor(typeId: number): string {
    const type = this.activityTypes.find(t => t.id === typeId);
    return type?.color || '#6c757d';
  }

  getActivityTypeIcon(typeId: number): string {
    const type = this.activityTypes.find(t => t.id === typeId);
    return type?.icon || 'fas fa-circle';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return this.formatDate(dateString);
  }

  getThisMonthCount(): number {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    
    return this.activities.filter(activity => {
      const activityDate = new Date(activity.activity_date || activity.created_at || '');
      return activityDate.getMonth() === thisMonth && activityDate.getFullYear() === thisYear;
    }).length;
  }

  getFollowUpCount(): number {
    const now = new Date();
    return this.activities.filter(activity => {
      if (!activity.follow_up_date) return false;
      const followUpDate = new Date(activity.follow_up_date);
      return followUpDate <= now;
    }).length;
  }

  resetForm(): void {
    this.newActivity = {
      type_id: 0,
      description: '',
      activity_date: '',
      follow_up_date: '',
      outcome: ''
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
